import { createContext, useContext, useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import {
  Outlet,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { api, STORAGE_USER, TOKEN_API } from "../services/api";

import { IUser } from "../types";
import { Loading } from "../components/Loading";
import { App } from "antd";

type LoginResponse = {
  user: IUser;
  token: {
    type: string;
    token: string;
  };
};

type SignInCredentials = {
  username: string;
  password: string;
};

type AuthContextData = {
  user: IUser | null;
  isAuthenticated: boolean;
  signIn(
    credentials: SignInCredentials
  ): Promise<{ username: string; password: string } | void | undefined>;
  signOut(): Promise<void>;
  refreshUserData: (data: IUser) => void;
  isLoading: boolean;
};

const AuthContext = createContext({} as AuthContextData);

const AuthProvider = () => {
  const { message } = App.useApp();

  const navigate = useNavigate();
  const location = useLocation();
  const data = useLoaderData() as IUser | undefined;

  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);
  const isAuthenticated = !!user;

  useEffect(() => {
    if (data) {
      setUser(data);
    }
    setLoading(false);
  }, [data]);

  const signIn = async ({ username, password }: SignInCredentials) => {
    setLoading(true);
    return await api
      .post("/login", {
        username: username.trim().toLowerCase(),
        password: password.trim(),
      })
      .then(({ data }: AxiosResponse<LoginResponse>) => {
        const { token, user } = data;

        if (user.profile !== "ADMINISTRATOR" && user.profile !== "ATTENDANT") {
          message.error("Acesso não autorizado para este perfil.");
          setLoading(false);
          return;
        }

        setUser(user);
        api.defaults.headers.common["Authorization"] = `Bearer ${token.token}`;

        localStorage.setItem(STORAGE_USER, JSON.stringify(user));
        localStorage.setItem(TOKEN_API, token.token);
        const origin = location.state?.from?.pathname || "/";
        navigate(origin, { replace: true });
        setLoading(false);
        return;
      })
      .catch(({ response }) => {
        if (response.status === 401) {
          message.error(response.data);
        } else {
          message.error("Algo inesperado ocorreu!");
        }

        throw new Error();
      });
  };

  const signOut = async () => {
    setLoading(true);
    await api.post("/logout").finally(() => {
      setUser(null);
      localStorage.removeItem(STORAGE_USER);
      localStorage.removeItem(TOKEN_API);
      navigate("/login");
      setLoading(false);
    });
  };

  function refreshUserData(data: IUser) {
    setUser({ ...user, ...data });
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        signIn,
        signOut,
        refreshUserData,
        isLoading: loading,
      }}
    >
      {loading ? <Loading /> : <Outlet />}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  return useContext(AuthContext);
};

export { useAuth, AuthProvider, AuthContext };
